import CarHireUpsellOfferTile from '../CarHireOfferTile/CarHireUpsellOfferTile'
import React, { useCallback, useMemo } from 'react'
import CarHireUpsellOfferTileLoadingSkeleton from '../CarHireOfferTile/CarHireUpsellOfferTileLoadingSkeleton'
import { addToCart } from 'analytics/snowplow/events'
import { navigateToCheckout } from 'checkout/actions/navigateActions'
import { initialiseCheckout } from 'actions/CheckoutActions'
import { generateCarHireCheckoutItem } from '../CarHireCheckoutHelper'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import moment from 'moment'
import { sortCarHireOffers } from '../carHireUtils'
import { take } from 'lib/array/arrayUtils'
import useCarHireList from 'hooks/CarHire/useCarHireList'
import { getDefaultUserDriverCategory } from 'selectors/carHireSelectors'
import useOrderItemsEligibleForCarHireBundledDiscount from 'hooks/CarHire/useOrderItemsEligibleForCarHireBundledDiscount'
import * as Analytics from 'analytics/analytics'
import useCarHireOfferListFilters from 'hooks/CarHire/useCarHireOfferListFilters'
import CarHireDiscountUpcomingOrderCarouselWrapper from './CarHireDiscountUpcomingOrderCarouselWrapper'

interface Props {
  order: App.Order;
  showOfferBookNowButton?: boolean
  variant?: 'card' | 'full-width'
}

function CarHireDiscountUpcomingOrderCarousel(props: Props) {
  const { order, variant = 'full-width', showOfferBookNowButton } = props

  const { earliestItem, latestItem, place } = useOrderItemsEligibleForCarHireBundledDiscount(order.id)

  const dispatch = useAppDispatch()

  const [defaultAgeCategory, defaultAge] = useAppSelector(getDefaultUserDriverCategory)

  const filters = useCarHireOfferListFilters({
    pickUpLocationId: place?.id,
    dropOffLocationId: place?.id,
    pickUpDate: earliestItem?.startDate || undefined,
    dropOffDate: latestItem?.endDate || undefined,
    pickUpTime: '10:00',
    dropOffTime: '10:00',
    driversAgeCategory: defaultAgeCategory,
    isPickUpAtAirport: false,
    pickUpSearchType: 'geo',
    dropOffSearchType: 'geo',
    driversAge: defaultAge,
    bundledOrderId: order.id,
  })

  const carHireList = useCarHireList(filters, 0)

  const sortedOffers = useMemo(() => take(sortCarHireOffers(carHireList.vehicles, 'price.asc'), 6), [carHireList.vehicles])

  const onGoToCheckout = useCallback((offer: App.CarHireOffer) => {
    if (filters) {
      const [pickUpDate, pickUpTime] = moment.utc(carHireList.pickUp.date).format('YYYY-MM-DD|HH:mm').split('|')
      const [dropOffDate, dropOffTime] = moment.utc(carHireList.dropOff.date).format('YYYY-MM-DD|HH:mm').split('|')

      const item = generateCarHireCheckoutItem(offer, {
        dropOffDate,
        dropOffTime,
        dropOffLocation: place?.canonicalName,
        dropOffLocationId: place?.id || '',
        pickUpLocation: place?.canonicalName,
        pickUpLocationId: place?.id || '',
        pickUpDate,
        pickUpTime,
        ageCategory: filters.driversAgeCategory || 2,
        age: filters.driversAge || 25,
        isPickUpAtAirport: !!filters.isPickUpAtAirport,
        resultsTimestamp: carHireList.timestamp || 0,
      })

      dispatch(initialiseCheckout([item]))
      Analytics.trackEvent(addToCart())
      dispatch(navigateToCheckout())
    }
  }, [dispatch, filters, place, carHireList])

  return <CarHireDiscountUpcomingOrderCarouselWrapper variant={variant}>
    {carHireList.fetching && <>
      <CarHireUpsellOfferTileLoadingSkeleton />
      <CarHireUpsellOfferTileLoadingSkeleton />
      <CarHireUpsellOfferTileLoadingSkeleton />
      <CarHireUpsellOfferTileLoadingSkeleton />
    </>}
    {!carHireList.fetching && sortedOffers.map(offer => (
      <CarHireUpsellOfferTile key={offer.id} offer={offer} onOfferClick={onGoToCheckout} showBookNow={showOfferBookNowButton} />
    ))}
  </CarHireDiscountUpcomingOrderCarouselWrapper>
}

export default CarHireDiscountUpcomingOrderCarousel
